/**
 * Policy Management Page - Admin page to manage claim limits/policies
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { reimbursementApi } from '../api/reimbursementApi'
import type {
  ReimbursementPolicy,
  CreatePolicyInput,
  UpdatePolicyInput,
} from '../types/reimbursement.types'
import LoadingPage from '@/shared/components/LoadingPage'

const formatCurrency = (amount: number, currency: string = 'IDR') => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

const emptyPolicyForm: CreatePolicyInput = {
  name: '',
  description: '',
  category_id: '',
  employee_grade: 'ALL',
  employment_type: 'all',
  limit_amount: 0,
  limit_period: 'yearly',
  requires_receipt: true,
  min_receipt_amount: 0,
  requires_approval: true,
  approval_threshold: undefined,
  effective_from: new Date().toISOString().split('T')[0],
  effective_to: undefined,
  prorate_for_new_joiners: true,
  prorate_for_exits: true,
  is_active: true,
}

const PolicyManagementPage = () => {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState<ReimbursementPolicy | null>(null)
  const [formData, setFormData] = useState<CreatePolicyInput>(emptyPolicyForm)
  const [showInactive, setShowInactive] = useState(false)

  // Fetch policies
  const { data: policiesData, isLoading: policiesLoading } = useQuery({
    queryKey: ['reimbursementPolicies', showInactive],
    queryFn: () => reimbursementApi.getPolicies(),
  })

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: reimbursementApi.getCategories,
  })

  const policies = policiesData?.data || []
  const categories = categoriesData?.data || []

  // Filter policies based on showInactive
  const filteredPolicies = showInactive
    ? policies
    : policies.filter((p) => p.is_active)

  // Initialize form when editing
  useEffect(() => {
    if (editingPolicy) {
      setFormData({
        name: editingPolicy.name,
        description: editingPolicy.description || '',
        category_id: editingPolicy.category_id,
        employee_grade: editingPolicy.employee_grade || 'ALL',
        employment_type: (editingPolicy.employment_type as 'all' | 'permanent' | 'contract') || 'all',
        limit_amount: editingPolicy.limit_amount,
        limit_period: (editingPolicy.limit_period as 'monthly' | 'quarterly' | 'yearly' | 'per_claim') || 'yearly',
        requires_receipt: editingPolicy.requires_receipt ?? true,
        min_receipt_amount: editingPolicy.min_receipt_amount || 0,
        requires_approval: editingPolicy.requires_approval ?? true,
        approval_threshold: editingPolicy.approval_threshold || undefined,
        effective_from: editingPolicy.effective_from,
        effective_to: editingPolicy.effective_to || undefined,
        prorate_for_new_joiners: editingPolicy.prorate_for_new_joiners ?? true,
        prorate_for_exits: editingPolicy.prorate_for_exits ?? true,
        is_active: editingPolicy.is_active ?? true,
      })
      setShowForm(true)
    }
  }, [editingPolicy])

  // Mutations
  const createMutation = useMutation({
    mutationFn: reimbursementApi.createPolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementPolicies'] })
      toast.success('Policy created successfully')
      resetForm()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to create policy')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePolicyInput }) =>
      reimbursementApi.updatePolicy(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementPolicies'] })
      toast.success('Policy updated successfully')
      resetForm()
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to update policy')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: reimbursementApi.deletePolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reimbursementPolicies'] })
      toast.success('Policy deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete policy')
    },
  })

  const resetForm = () => {
    setFormData(emptyPolicyForm)
    setEditingPolicy(null)
    setShowForm(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.category_id || !formData.limit_amount || !formData.effective_from) {
      toast.error('Please fill all required fields')
      return
    }

    if (editingPolicy) {
      updateMutation.mutate({ id: editingPolicy.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (policy: ReimbursementPolicy) => {
    if (confirm(`Are you sure you want to delete the policy "${policy.name}"?`)) {
      deleteMutation.mutate(policy.id)
    }
  }

  const getCategoryName = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId)
    return category?.name || 'Unknown'
  }

  if (policiesLoading) return <LoadingPage />

  return (
    <div className="p-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-rose-700 bg-clip-text text-transparent">
              Claim Policies
            </h1>
            <p className="text-gray-500 mt-1">Configure reimbursement limits and eligibility rules</p>
          </div>
          <button
            onClick={() => {
              resetForm()
              setShowForm(true)
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-xl hover:from-red-700 hover:to-rose-700 shadow-lg shadow-red-500/25 transition-all duration-200 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Policy
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{policies.length}</p>
                <p className="text-sm text-gray-500">Total Policies</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{policies.filter(p => p.is_active).length}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
                <p className="text-sm text-gray-500">Categories</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
          />
          Show inactive policies
        </label>
        <p className="text-sm text-gray-500">
          Showing {filteredPolicies.length} of {policies.length} policies
        </p>
      </div>

      {/* Policy Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-700 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-white">
                  {editingPolicy ? 'Edit Policy' : 'Create New Policy'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Policy Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    placeholder="e.g., Transportation - All Employees"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Expense Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white"
                      required
                      disabled={!!editingPolicy}
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Limit Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Limit Amount (IDR) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={formData.limit_amount || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, limit_amount: parseFloat(e.target.value) || 0 })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                      min="0"
                      required
                    />
                  </div>

                  {/* Limit Period */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Limit Period
                    </label>
                    <select
                      value={formData.limit_period}
                      onChange={(e) => setFormData({ ...formData, limit_period: e.target.value as 'monthly' | 'quarterly' | 'yearly' | 'per_claim' })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                      <option value="per_claim">Per Claim</option>
                    </select>
                  </div>

                  {/* Employee Grade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Employee Grade
                    </label>
                    <input
                      type="text"
                      value={formData.employee_grade}
                      onChange={(e) => setFormData({ ...formData, employee_grade: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                      placeholder="ALL, L1, L2, Manager, etc."
                    />
                  </div>

                  {/* Employment Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Employment Type
                    </label>
                    <select
                      value={formData.employment_type}
                      onChange={(e) => setFormData({ ...formData, employment_type: e.target.value as 'all' | 'permanent' | 'contract' })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white"
                    >
                      <option value="all">All Employees</option>
                      <option value="permanent">Permanent Only</option>
                      <option value="contract">Contract Only</option>
                    </select>
                  </div>

                  {/* Approval Threshold */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Approval Threshold (IDR)
                    </label>
                    <input
                      type="number"
                      value={formData.approval_threshold || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          approval_threshold: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                      placeholder="Auto-approve below this amount"
                      min="0"
                    />
                  </div>

                  {/* Effective From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Effective From <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.effective_from}
                      onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                      required
                    />
                  </div>

                  {/* Effective To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Effective To
                    </label>
                    <input
                      type="date"
                      value={formData.effective_to || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, effective_to: e.target.value || undefined })
                      }
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none"
                    rows={2}
                    placeholder="Brief description of this policy..."
                  />
                </div>

                {/* Checkboxes */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Policy Options</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.requires_receipt}
                        onChange={(e) => setFormData({ ...formData, requires_receipt: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Requires Receipt</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.requires_approval}
                        onChange={(e) => setFormData({ ...formData, requires_approval: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Requires Approval</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.prorate_for_new_joiners}
                        onChange={(e) =>
                          setFormData({ ...formData, prorate_for_new_joiners: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Prorate New Joiners</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.prorate_for_exits}
                        onChange={(e) =>
                          setFormData({ ...formData, prorate_for_exits: e.target.checked })
                        }
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Prorate Exits</span>
                    </label>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-700 text-white rounded-xl hover:from-red-700 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-lg shadow-red-500/25"
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : editingPolicy
                      ? 'Update Policy'
                      : 'Create Policy'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Policies Grid */}
      {filteredPolicies.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No policies found</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first claim policy.</p>
          <button
            onClick={() => {
              resetForm()
              setShowForm(true)
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Policy
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredPolicies.map((policy) => (
            <div
              key={policy.id}
              className={`bg-white rounded-2xl shadow-sm border transition-all duration-200 hover:shadow-md ${
                policy.is_active ? 'border-gray-100' : 'border-gray-200 bg-gray-50/50'
              }`}
            >
              <div className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left: Policy Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-xl ${policy.is_active ? 'bg-red-100' : 'bg-gray-200'}`}>
                        <svg className={`w-5 h-5 ${policy.is_active ? 'text-red-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-semibold ${policy.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                            {policy.name}
                          </h3>
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              policy.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}
                          >
                            {policy.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {policy.description && (
                          <p className="text-sm text-gray-500 mt-0.5 truncate">{policy.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="inline-flex items-center gap-1.5 text-gray-600">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {policy.category?.name || getCategoryName(policy.category_id)}
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-gray-600">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            {policy.employee_grade} / {policy.employment_type}
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-gray-600">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {format(new Date(policy.effective_from), 'MMM d, yyyy')}
                            {policy.effective_to && ` - ${format(new Date(policy.effective_to), 'MMM d, yyyy')}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Limit & Actions */}
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(policy.limit_amount)}</p>
                      <p className="text-sm text-gray-500 capitalize">{policy.limit_period?.replace('_', ' ')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingPolicy(policy)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(policy)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Policy Features */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                  {policy.requires_receipt && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-lg">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Receipt Required
                    </span>
                  )}
                  {policy.requires_approval && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-50 text-rose-700 text-xs font-medium rounded-lg">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Approval Required
                    </span>
                  )}
                  {policy.approval_threshold && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-lg">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Auto-approve below {formatCurrency(policy.approval_threshold)}
                    </span>
                  )}
                  {policy.prorate_for_new_joiners && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-lg">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      Prorated for New Joiners
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default PolicyManagementPage
